import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoreHorizontal, Eye, UploadCloud, Users, Pencil, Archive, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { UploadStatusPill, IndeterminateBar, fmtDate, plural, isProcessing } from "./ui";

const ACCENT = {
  completed: "bg-[#2EBD85]",
  partial: "bg-amber-400",
  failed: "bg-red-400",
  superseded: "bg-gray-300",
};

const Metric = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
    <p className="mt-0.5 text-sm font-semibold text-gray-900 tabular-nums truncate">{value}</p>
  </div>
);

const ActionButton = ({ icon: Icon, children, ...props }) => (
  <button
    type="button"
    {...props}
    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:text-[#0F4C81] hover:bg-[#F1F8F8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
  >
    <Icon className="w-3.5 h-3.5" /> {children}
  </button>
);

const TestListCard = ({ list, labNames, readOnly, onViewTests, onUpload, onManageLabs, onRename, onArchive, index = 0 }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const upload = list.latest_upload;
  const processing = isProcessing(upload);
  const labCount = list.assigned_lab_ids.length;
  const accent = processing ? "bg-sky-400" : upload?.stuck ? "bg-red-400" : ACCENT[upload?.status] || "bg-gray-200";

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => !menuRef.current?.contains(e.target) && setMenuOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index, 6) * 0.04 }}
      className="group relative bg-white rounded-2xl ring-1 ring-[#DCEAEA] hover:ring-[#00A6A6]/40 hover:shadow-[0_8px_24px_-12px_rgba(0,166,166,0.25)] transition-shadow overflow-hidden"
    >
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent} transition-colors`} />
      <div className="pl-5 pr-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-[#00A6A6] shrink-0" />
              <h3 className="text-sm font-semibold text-gray-900 truncate">{list.name}</h3>
              <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] font-semibold text-gray-500">v{list.version_count}</span>
            </div>
            <p className="mt-1 text-[11px] text-gray-400 truncate">
              {upload ? `Uploaded ${fmtDate(upload.uploaded_at)} by ${upload.uploaded_by}` : "No uploads yet"}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <UploadStatusPill upload={upload} />
            {!readOnly && (
              <div className="relative" ref={menuRef}>
                <button type="button" aria-label="More actions" onClick={() => setMenuOpen((v) => !v)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {menuOpen && (
                  <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                    className="absolute right-0 mt-1 w-40 z-20 rounded-xl bg-white shadow-lg ring-1 ring-black/5 py-1">
                    <button type="button" onClick={() => { setMenuOpen(false); onRename(list); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
                      <Pencil className="w-3.5 h-3.5" /> Rename
                    </button>
                    <button type="button" onClick={() => { setMenuOpen(false); onArchive(list); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50">
                      <Archive className="w-3.5 h-3.5" /> Archive list
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </div>
        </div>

        {processing && (
          <div className="mt-3 space-y-1.5">
            <IndeterminateBar />
            <p className="text-[11px] text-sky-700">Processing v{list.version_count} — labs keep their current tests until it finishes.</p>
          </div>
        )}
        {!processing && (upload?.status === "failed" || upload?.stuck) && (
          <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-2 text-[11px] text-red-700">
            <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
            {upload.stuck ? "Processing stopped responding. Upload the sheet again." : upload.failure_message || "The latest upload failed."}
            {list.test_count > 0 && " Labs still use the previous version."}
          </p>
        )}

        <div className="mt-3 grid grid-cols-3 gap-3">
          <Metric label="Tests" value={list.test_count.toLocaleString("en-IN")} />
          <Metric label="Labs" value={labCount} />
          <Metric label="Last sheet" value={upload ? `${upload.valid_rows}/${upload.total_rows} rows` : "—"} />
        </div>

        <p className="mt-2.5 text-[11px] text-gray-500 truncate" title={labNames.join(", ")}>
          {labCount ? <>Used by <span className="text-gray-700">{labNames.slice(0, 3).join(", ")}</span>{labCount > 3 && ` +${labCount - 3} more`}</> : "Not applied to any lab yet"}
        </p>

        {upload?.error_file && (
          <a href={upload.error_file} target="_blank" rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:underline">
            <AlertTriangle className="w-3 h-3" /> {plural(upload.invalid_rows, "row")} skipped — error report
          </a>
        )}
      </div>

      <div className="px-3 py-2 border-t border-[#EEF5F5] bg-[#FAFDFD] flex flex-wrap items-center gap-1">
        <ActionButton icon={Eye} onClick={() => onViewTests(list)} disabled={!list.test_count}>View tests</ActionButton>
        {!readOnly && (
          <>
            <ActionButton icon={UploadCloud} onClick={() => onUpload(list)} disabled={processing}>Upload new version</ActionButton>
            <ActionButton icon={Users} onClick={() => onManageLabs(list)} disabled={!list.test_count}>Manage labs</ActionButton>
          </>
        )}
      </div>
    </motion.article>
  );
};

export default TestListCard;
